import React, { useEffect } from 'react';
import produce from 'immer';
import type { EditorState } from 'lexical';
import { gql } from '@soundxyz/gql-string';
import { ERROR_TYPE, POSTING_ERROR_ACTIONS } from '@soundxyz/utils/constants/logging';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { LexicalDisplay } from '@components/Lexical/Display';
import { LexicalUtils } from '@components/Lexical/utils';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { commentReplyFilterQueryKey } from '@components/main-components/user/shared';
import { useModal } from '@context/modal';
import { useToast } from '@context/toast';
import {
  AddPostCommentReplyDocument,
  CommentRepliesDocument,
  PostModalCommentDocument,
  setInfiniteQueryData,
  useMutation,
  useQuery,
} from '@graphql/reactQuery';
import { useLexicalValidate } from '@hooks/useLexicalValidate';
import { useLogError } from '@hooks/useLogErrorNew';
import { ReplyPostContent } from './Content';

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation AddPostCommentReply($input: MutationAddPostCommentReplyInput!) {
    addPostCommentReply(input: $input) {
      __typename
      ... on MutationAddPostCommentReplySuccess {
        data {
          id
          ...PostCommentsRow
        }
      }
      ... on Error {
        message
      }
    }
  }

  query PostModalComment($postCommentId: UUID!) {
    postComment(commentId: $postCommentId) {
      __typename
      id
      createdAt
      content
      lexicalContent
      author {
        id
        username
        publicAddress
        webappUri
        displayName
        artist {
          id
          name
        }
        avatar {
          id
          url
        }
      }
      post {
        id
        __typename
        createdAt
        content
        lexicalContent
        creator {
          id
          username
          publicAddress
          webappUri
          displayName
          artist {
            id
            name
          }
          avatar {
            id
            url
          }
        }
        ... on ReleasePost {
          id
          ...TopReleasePost
        }
        ... on PlaylistPost {
          id
          ...TopPlaylistPost
        }
      }
    }
  }
`);

export function ReplyPostCommentModal({ postCommentId }: { postCommentId: string }) {
  const { openToast } = useToast();
  const { closeModal } = useModal();

  const logError = useLogError();

  const { mutateAsync: postReply, isLoading: postingReply } = useMutation(
    AddPostCommentReplyDocument,
    {
      retry: 3,
    },
  );

  const { data, isLoading } = useQuery(PostModalCommentDocument, {
    variables: {
      postCommentId,
    },
    staleTime: 0,
  });

  const postAuthor = data?.data.postComment?.author;
  const postContent = data?.data.postComment?.content;
  const lexicalContent = useLexicalValidate(data?.data?.postComment?.lexicalContent);

  const [hasOverflow, setHasOverflow] = React.useState(false);
  const bodyRef = React.useRef<HTMLDivElement>(null);

  const submitReply = useStableCallback(async (lexicalState: EditorState) => {
    const contentText = LexicalUtils.generateTextForState(lexicalState);
    if (!contentText.length) return;
    try {
      const {
        data: { addPostCommentReply },
      } = await postReply({
        input: {
          postCommentId,
          lexicalContent: lexicalState.toJSON(),
        },
      });

      if (addPostCommentReply.__typename === 'MutationAddPostCommentReplySuccess') {
        openToast({
          text: 'Your reply was added',
          variant: 'success',
        });

        setInfiniteQueryData(
          {
            query: CommentRepliesDocument,
            filterQueryKey: commentReplyFilterQueryKey(postCommentId),
            options: {},
          },
          previousData => {
            const page = {
              data: {
                commentReplies: {
                  edges: [{ node: addPostCommentReply.data }],
                  pageInfo: {
                    endCursor: null,
                    hasNextPage: true,
                  },
                },
              },
            } satisfies NonNullable<typeof previousData>['pages'][number];

            if (!previousData) {
              return {
                pageParams: [],
                pages: [page],
              };
            }

            return produce(previousData, draft => {
              draft.pages.unshift(page);
            });
          },
        );

        closeModal();
      } else {
        logError({
          action: POSTING_ERROR_ACTIONS.REPLY_POST_COMMENT_FAILED,
          error: new Error(addPostCommentReply.message),
          // Swap for REPLY_ERROR
          errorType: ERROR_TYPE.UNKNOWN,
          isArtistOnlyPage: false,
          level: 'error',
          message: addPostCommentReply.message,
          pillar: 'POSTING',
          toast: addPostCommentReply.message,
          indexedTags: {
            postCommentId,
            content: contentText,
          },
        });
        return;
      }
    } catch (error) {
      logError({
        action: POSTING_ERROR_ACTIONS.REPLY_POST_COMMENT_FAILED,
        error,
        // Swap for REPLY_ERROR
        errorType: ERROR_TYPE.UNKNOWN,
        isArtistOnlyPage: false,
        level: 'error',
        message: 'There was an error replying to a comment',
        pillar: 'POSTING',
        toast: 'There was an error posting your reply. Please try again.',
        indexedTags: {
          postCommentId,
          content: contentText,
        },
      });
    }
  });

  useEffect(() => {
    if (!bodyRef.current) {
      return;
    }
    setHasOverflow(bodyRef.current.clientHeight < bodyRef.current.scrollHeight);
  }, [bodyRef]);

  return (
    <StandardModal
      ref={bodyRef}
      mobileFullScreen
      title="Post your reply"
      body={
        <ReplyPostContent
          isSubmitting={postingReply}
          onSubmit={submitReply}
          isLoading={isLoading}
          postCreator={postAuthor}
          postContent={
            lexicalContent ? (
              <LexicalDisplay showMentionsHoverCard={false} state={lexicalContent} />
            ) : (
              postContent
            )
          }
          autoFocus
        />
      }
      closeModal={closeModal}
      bodyClassName="md:overflow-auto"
      showHeaderShadow={hasOverflow}
      preventClose={postingReply}
      bodyWithoutPadding
    />
  );
}
